import React from "react";
import PropTypes from "prop-types";
import { DateTime } from "luxon";

const Calendar = ({
  startOfMonth,
  fairDays,
  webReservePeriodDate,
  diffMonths = 3,
  selectedDate = [],
  initializeDate = "",
  type = "",
  holidays = [],
}) => {
  const MAX_WEEK = 6;
  const MAX_WEEKLY = 7;

  const calendarDayClass = (
    month,
    currentDate,
    fairDaysData,
    reservePeriodDate,
  ) => {
    let dayClass = "is-disabled";
    const toDay = DateTime.now().toFormat("yyyy-MM-dd");
    let reservableDay = DateTime.fromFormat(toDay, "yyyy-MM-dd");
    const dateString = currentDate.toFormat("yyyy-MM-dd");

    if (reservePeriodDate > 0) {
      reservableDay = reservableDay.plus({ days: reservePeriodDate });
    }

    // カレンダーの月ではない場合
    if (currentDate.toFormat("MM") !== month) {
      dayClass = "is-empty";
      return dayClass;
    }

    // 予約可能な日を過ぎた場合
    if (currentDate < reservableDay) {
      return dayClass;
    }

    if (fairDaysData[dateString]) {
      // 日付ごとの残席状況表示
      const fairDayValues = Object.values(fairDaysData[dateString]);
      let existsFreeTime = false;
      let existsFewTime = false;
      for (let i = 0; i < fairDayValues.length; i += 1) {
        const fairDay = fairDayValues[i];

        if (fairDay.times.some((time) => time.remainder === 0)) {
          existsFreeTime = true;
          break; // 一つでも⚪︎になる条件があれば⚪︎表示確定なのでbreakする
        }

        if (fairDay.times.some((time) => time.remainder === 1)) {
          existsFewTime = true;
        }
      }
      // 空席が1つ以上あるとき
      if (existsFreeTime) {
        dayClass = "is-accepting";
      } else if (existsFewTime) {
        // 空席がなく、混雑が1つ以上あるとき
        dayClass = "is-few";
      } else {
        dayClass = "is-full";
      }
    }

    return dayClass;
  };

  // 土日祝日のクラスを設定
  const setHolidayClass = (date) => {
    let holidayClass = " background-color--page_bg";

    // luxonでは日曜日が7、土曜日が6
    if (holidays.includes(date.toFormat("yyyy-MM-dd")) || date.weekday === 7) {
      holidayClass = " is-holiday background-color--calendar_sun_bg"; // 日曜日
    } else if (date.weekday === 6) {
      holidayClass = " is-sat background-color--calendar_sat_bg"; // 土曜日
    }
    return holidayClass;
  };

  const weekJa = (date) => {
    return date.weekdayShort;
  };

  return (
    <div className="calendar" id={type ? `js-calendar_${type}` : ""}>
      <div className="calendar__bg">
        <a className="calendar__close" href="#">
          ×
        </a>
      </div>
      <div
        className="calendar__inner js-slick__list background-color--contents_bg"
        id="calendar__inner"
      >
        {[...Array(diffMonths)].map((_, monthIndex) => {
          const month = DateTime.fromISO(startOfMonth).plus({
            months: monthIndex,
          });
          // 最初の週の日曜日になる日付(※先月になる場合はis-emptyクラスでブランク表示)
          const startDate = DateTime.fromISO(startOfMonth)
            .plus({ months: monthIndex })
            .startOf("month")
            .minus({
              days:
                DateTime.fromISO(startOfMonth)
                  .plus({ months: monthIndex })
                  .startOf("month").weekday % 7,
            });
          return (
            <div
              className="calendar__item background-color--contents_bg"
              key={monthIndex}
            >
              <div className="calendar__item-head color--accent font-family--middle">
                {month.toFormat("yyyy")}/
                <span className="calendar__month">{month.toFormat("MM")}</span>
              </div>
              <div className="calendar__item-body">
                <ul className="calendar__week-list">
                  {["日", "月", "火", "水", "木", "金", "土"].map(
                    (day, index) => (
                      <li className="calendar__week-item" key={index}>
                        {day}
                      </li>
                    ),
                  )}
                </ul>
                <ul className="calendar__day-list">
                  {[...Array(MAX_WEEK)].map((_i, weekIndex) =>
                    [...Array(MAX_WEEKLY)].map((_j, dayIndex) => {
                      const dayId = weekIndex * MAX_WEEKLY + dayIndex;
                      const currentDate = startDate.plus({ days: dayId });
                      const dayClass = calendarDayClass(
                        month.toFormat("MM"),
                        currentDate,
                        fairDays,
                        webReservePeriodDate,
                      );
                      const formattedDate = currentDate.toFormat("yyyy-MM-dd");
                      const disabled =
                        dayClass.includes("disabled") ||
                        dayClass.includes("is-full");
                      const isChecked =
                        formattedDate === initializeDate &&
                        selectedDate.length === 0
                          ? true // 初期値の日付（日付順）が存在して、カレンダーから選択された日付がないときに初期値の日付をcheckedにする
                          : selectedDate.includes(formattedDate); // それ以外の場合、選択された日付をcheckedにする
                      return (
                        <li
                          className={`calendar__day-item ${dayClass}${!isChecked ? setHolidayClass(currentDate) : ""}`}
                          key={`${weekIndex}-${dayIndex}`}
                        >
                          {dayClass !== "is-empty" && (
                            <label className="calendar__day-check">
                              <input
                                type="checkbox"
                                name="date[]"
                                value={formattedDate}
                                disabled={disabled}
                                checked={isChecked}
                                data-date={`${currentDate.toFormat("yyyy年MM月dd日")}(${weekJa(currentDate)})`}
                                className="calendar__day-input"
                                readOnly
                              />
                              <span
                                className={`calendar__day-check-inner border-color--text-before ${isChecked ? "background-color--sub" : ""}`}
                              >
                                {currentDate.toFormat("d")}
                              </span>
                            </label>
                          )}
                        </li>
                      );
                    }),
                  )}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * startOfMonth: カレンダー開始日(YYYY-mm-dd)
 * fairDays: 開催日付と時間一覧
 * webReservePeriodDate: オンライン予約の受付期限（日数）
 * diffMonths: カレンダーの表示月
 * selectedDate: 選択されている日付
 * initializeDate: 初期値の日付データ（日付順）
 * type: カレンダータイプ
 */
Calendar.propTypes = {
  startOfMonth: PropTypes.string.isRequired,
  fairDays: PropTypes.object.isRequired,
  webReservePeriodDate: PropTypes.string.isRequired,
  diffMonths: PropTypes.number,
  selectedDate: PropTypes.array,
  initializeDate: PropTypes.string,
  type: PropTypes.string,
};

export default Calendar;
